<script>
import { cloneDeep } from 'lodash-es';
import { mapActions, mapState } from 'vuex';
import ItineraryPricesPriceIntervalSettings from './ItineraryPricesPriceIntervalSettings';
import ItineraryPricesTransportSearchBar from './ItineraryPricesTransportSearchBar';
import ItineraryPricesSelectedTransports from './ItineraryPricesSelectedTransports';
import ItineraryPricesPriceSettingsPax from './ItineraryPricesPriceSettingsPax';
import ItineraryPricesPriceSettingsTable from './ItineraryPricesPriceSettingsTable';
import ItineraryPricesTransportSearchFeed from './ItineraryPricesTransportSearchFeed';

export default {
  name: 'ItineraryPricesWrapper',
  components: {
    ItineraryPricesPriceIntervalSettings,
    ItineraryPricesTransportSearchBar,
    ItineraryPricesSelectedTransports,
    ItineraryPricesPriceSettingsPax,
    ItineraryPricesPriceSettingsTable,
    ItineraryPricesTransportSearchFeed,
  },
  props: {
    itineraryPrices: {
      type: Array,
      default: () => [],
    },
    transits: {
      type: Array,
      required: true,
    },
    carriers: {
      type: Array,
      required: true,
    },
    dayProgram: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localItineraryPrice: [],
      selectedPriceIntervalIndex: 0,
      headers: null,
      ageDiscountPaxes: [],
      groupPaxes: [],
      localPriceSettings: {},
      transportStatus: {
        optional: 0,
        alternative: 1,
        primary: 2,
      },
    };
  },
  created() {
    this.getSuppliersWithCurrencyBySupplierType();
    if (this.itineraryPrices.length) {
      this.localItineraryPrice = cloneDeep(this.itineraryPrices);
      this.getItineraryPriceSettings(this.localItineraryPrice[0].id);
    }
  },
  computed: {
    ...mapState({
      priceSettings: state => state.itinerary.priceSettings.items,
      priceSettingsLoading: state => state.itinerary.priceSettings.loading,
      transports: state => state.nonDynamicTransport.searchTransports.items,
    }),
  },
  watch: {
    priceSettings: {
      immediate: true,
      deep: true,
      handler(value) {
        this.localPriceSettings = cloneDeep(value);
      },
    },
    selectedPriceIntervalIndex: {
      deep: true,
      handler(value) {
        if (this.localItineraryPrice.length &&
          this.localItineraryPrice[value].id) {
          this.getItineraryPriceSettings(this.localItineraryPrice[value].id);
          this.ageDiscountPaxes = [];
          this.groupPaxes = [];
        }
      },
    },
    itineraryPrices() {
      this.localItineraryPrice = cloneDeep(this.itineraryPrices);
      this.getItineraryPriceSettings(
        this.localItineraryPrice[this.selectedPriceIntervalIndex].id,
      );
    },
  },
  methods: {
    ...mapActions({
      syncItineraryPriceIntervals: 'itinerary/syncItineraryPriceIntervals',
      getSuppliersWithCurrencyBySupplierType: 'nonDynamicTransport/getSuppliersWithCurrencyBySupplierType',
      getItineraryPriceSettings: 'itinerary/getItineraryPriceSettings',
      searchTransportsAllotments: 'nonDynamicTransport/searchTransports',
    }),
    setPriceIntervals(value) {
      this.localItineraryPrice = value;
    },
    setSelectedInterval(value) {
      this.selectedPriceIntervalIndex = value;
    },
    updateSelectedAllotmentPeriods(value) {
      this.localItineraryPrice[this.selectedPriceIntervalIndex]
        .transportAllotments = [...value];
    },
    setMaxAllotment(value) {
      this.localItineraryPrice[this.selectedPriceIntervalIndex]
        .maxAllotments = +value;
    },
    syncPriceIntervals() {
      const domainId = +this.$route.params.domainId;
      const itineraryId = +this.$route.params.id;
      const priceIntervals = this.localItineraryPrice;
      const payload = { domainId, itineraryId, priceIntervals };
      this.syncItineraryPriceIntervals(payload);
    },
    setHeaders(value) {
      this.headers = value;
    },
    setAgeDiscountPaxes(value) {
      this.ageDiscountPaxes = value;
    },
    setGroupPaxes(value) {
      this.groupPaxes = value;
    },
    changeIntervalActiveStatus(priceIntervalIndex) {
      this.localItineraryPrice[priceIntervalIndex].active =
        !this.localItineraryPrice[priceIntervalIndex].active;
    },
    searchTransports(searchBody) {
      this.searchTransportsAllotments(searchBody);
    },
    addTransportAllotment(transportAllotment) {
      const { transportAllotments } = this.localItineraryPrice[this.selectedPriceIntervalIndex];
      let defaultIncluded = !transportAllotments.length
        ? this.transportStatus.primary
        : this.transportStatus.optional;

      if (transportAllotments.length) {
        const primaryTransport = transportAllotments.find(e => e.included === this.transportStatus.primary);
        const selectedTransportOutbound = transportAllotment.transport.outbound;
        const primaryTransportOutbound = primaryTransport.transportAllotment.transport.outbound;

        if (selectedTransportOutbound[selectedTransportOutbound.length - 1].arrivalTransitId ===
          primaryTransportOutbound[0].departureTransitId
        ) {
          defaultIncluded = this.transportStatus.optional;
        } else {
          defaultIncluded = this.transportStatus.alternative;
        }
      }

      transportAllotments.push({
        id: transportAllotment.id,
        included: defaultIncluded,
        transportAllotment,
      });
    },
    removeTransportAllotment(transportAllotment) {
      const { transportAllotments } = this.localItineraryPrice[this.selectedPriceIntervalIndex];
      const transportAllotmentIndex = transportAllotments.find(allotment =>
        allotment.transportAllotment.id === transportAllotment.id,
      );
      transportAllotments.splice(transportAllotmentIndex, 1);
    },
  },
};
</script>

<template>
  <div>
    <v-card class="pa-3">
      <itinerary-prices-price-interval-settings
        :price-intervals="localItineraryPrice"
        :minimum-age="localItineraryPrice.minimumAge"
        :itinerary-length="dayProgram.length"
        @update="setPriceIntervals"
        @setSelectedInterval="setSelectedInterval"
        @change-active-status="changeIntervalActiveStatus"
      />
      <v-divider class="mb-4" />
      <itinerary-prices-transport-search-bar
        v-if="localItineraryPrice.length"
        :selected-price-interval="localItineraryPrice[selectedPriceIntervalIndex]"
        :departure-date="localItineraryPrice[selectedPriceIntervalIndex].arrivalFrom"
        :carriers="carriers"
        :transits="transits"
        :itinerary-length="dayProgram.length"
        @search="searchTransports"
      />
      <itinerary-prices-transport-search-feed
        v-if="localItineraryPrice.length"
        :transport-search-results="transports"
        :selected-allotments="localItineraryPrice[selectedPriceIntervalIndex]
          .transportAllotments"
        @add-transport="addTransportAllotment"
        @remove-transport="removeTransportAllotment"
      />
      <itinerary-prices-selected-transports
        v-if="localItineraryPrice[selectedPriceIntervalIndex]"
        :allotments="localItineraryPrice[selectedPriceIntervalIndex].transportAllotments"
        :max-allotments="localItineraryPrice[selectedPriceIntervalIndex].maxAllotments"
        @update="updateSelectedAllotmentPeriods"
        @setMaxAllotment="setMaxAllotment"
      />
      <v-btn class="mt-3" color="success" @click="syncPriceIntervals">
        Save price ranges
      </v-btn>
    </v-card>
    <v-card v-if="localItineraryPrice.length" class="pa-3 mt-2">
      <div class="text-xs-center">
        <v-progress-circular
          v-if="priceSettingsLoading"
          color="primary"
          indeterminate
        />
      </div>
      <div
        v-if="!priceSettingsLoading && localItineraryPrice.length && localItineraryPrice[selectedPriceIntervalIndex].id"
      >
        <itinerary-prices-price-settings-pax
          v-if="localItineraryPrice.length"
          :age-discounts="ageDiscountPaxes"
          :group-paxes="groupPaxes"
          @update="setHeaders"
          @ageDiscount="setAgeDiscountPaxes"
        />
        <itinerary-prices-price-settings-table
          v-if="headers"
          :selected-price-interval="localItineraryPrice[selectedPriceIntervalIndex]"
          :start-date="localItineraryPrice[selectedPriceIntervalIndex].startDate"
          :price-interval-settings="localPriceSettings"
          :pax-headers="headers"
          :day-program="dayProgram"
          :carriers="carriers"
          :transits="transits"
          :age-discount-paxes="ageDiscountPaxes"
          @ageDiscount="setAgeDiscountPaxes"
          @group-paxes="setGroupPaxes"
        />
      </div>
      <div v-if="!localItineraryPrice[selectedPriceIntervalIndex].id" class="text-xs-center">
        <h5>You have selected a price interval that hasn't been saved, please save to create its prices</h5>
      </div>
    </v-card>
  </div>
</template>



// WEBPACK FOOTER //
// src/components/itinerary/itineraryPrices/ItineraryPricesWrapper.vue